import type { Step as RcStep } from '@sb/remote-control/types';

import {
  type ValidationRule,
  type RoutineView,
  type ValidationContext,
} from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class LocateRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Locate', 'Locate');
  }

  protected validate({ args, routine, ctx }: CanonicalValidateArgs): void {
    type LocArgs = Extract<RcStep.Arguments, { argumentKind: 'Locate' }>;
    const a = args as LocArgs;
    this.checkPlaneSelected(a);
    this.checkPlaneValid(a, routine, ctx);
    this.checkPositionListName(a);
  }

  /**
   * Validate that a plane is selected.
   * Throws ActionRequiredError if not set.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneSelected(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.planeID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No workplane selected',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the selected plane exists in routine or global space.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneValid(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    routine: RoutineView,
    ctx: ValidationContext,
  ): void {
    const allSpace = this.getSpaceItems(routine, ctx);

    if (
      !allSpace.some(
        (item) => item.id === args.planeID && item.kind === 'plane',
      )
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected workplane is not valid',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the position list name is set.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkPositionListName(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.positionListName) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list name is not configured',
        fieldId: 'positionListName',
      });
    }
  }
}

export const LocateValidation: ValidationRule =
  new LocateRule().toValidationRule();
